import {
  QuestionId,
  QuizModuleId,
  CorrectOrIncorrect,
  QuizModule,
  // quizModules,
  Age,
  Question,
} from "@common";
import { TestTakerSettings } from "@natklok/contexts/testTakerSettingsContext";
import { QuizSlideDataSequence } from "@natklok/interfaces/question.interface";
import getQuizModuleSlides from "./getQuizModuleSlides";
import { getAllCorrectVariants, getQuestionFromVariant } from "./questionUtils";
import { tryToParseStorageKeyValue } from "./webStorage";

export class SerializedQuizModuleData {
  correct: QuestionId[] = [];
  incorrect: QuestionId[] = [];

  constructor(public quizModuleId: QuizModuleId) {}
}

export class QuizModuleData {
  correct: QuestionId[] = [];
  incorrect: QuestionId[] = [];
  quizModuleId: QuizModuleId;
  quizModule: QuizModule;

  constructor({
    moduleData,
    quizModules,
  }: {
    moduleData: SerializedQuizModuleData;
    quizModules: Record<string, QuizModule>;
  }) {
    this.correct = moduleData.correct;
    this.incorrect = moduleData.incorrect;
    this.quizModuleId = moduleData.quizModuleId;
    this.quizModule = quizModules[this.quizModuleId];
  }

  getId() {
    return this.quizModuleId;
  }

  getSerialized(): SerializedQuizModuleData {
    return { correct: this.correct, incorrect: this.incorrect, quizModuleId: this.quizModuleId };
  }

  getAmountCorrect({
    age,
    questions,
    quizModules,
  }: {
    age: Age;
    questions: Question[];
    quizModules: Record<string, QuizModule>;
  }) {
    const suitableSlides = this.quizModule.slides.filter(
      (variant) =>
        getQuestionFromVariant({ variantInSvOrEn: variant, age, questions }) !== undefined
    );

    return (
      suitableSlides.length -
      this.getIncorrectlyAnsweredSlides({ age, questions, quizModules }).length
    );
  }

  getAttempted() {
    return this.correct.length + this.incorrect.length !== 0;
  }

  getIncorrectlyAnsweredSlides({
    age,
    questions,
    quizModules,
  }: {
    age: Age;
    questions: Question[];
    quizModules: Record<string, QuizModule>;
  }): QuizSlideDataSequence {
    // TODO COMPLETE: Add questions
    // TODO COMPLETE: Add quiz modules
    const quizModuleSlides = getQuizModuleSlides({
      quizModuleId: this.quizModuleId,
      age,
      questions,
      quizModules,
    });
    const allCorrectlyAnsweredVariants = getAllCorrectVariants();

    const incorrectlyAnsweredSlides = quizModuleSlides.filter((slideData) => {
      const good =
        slideData.question === undefined || // if slide isn't a question, always show it
        (this.hasCorrectlyAnswered(slideData.question.id.sv) === false &&
          allCorrectlyAnsweredVariants.includes(slideData.question.variant.sv) === false);

      return good;
    });

    return incorrectlyAnsweredSlides;
  }

  getIsCompleted({
    settings,
    quizModules,
    questions,
  }: {
    settings: TestTakerSettings;
    quizModules: Record<string, QuizModule>;
    questions: Question[];
  }) {
    return (
      this.correct.length >=
      getQuizModuleSlides({
        quizModuleId: this.quizModuleId,
        age: settings.age,
        quizModules,
        questions,
      }).length
    );
  }

  getIsUncompleted({
    settings,
    quizModules,
    questions,
  }: {
    settings: TestTakerSettings;
    quizModules: Record<string, QuizModule>;
    questions: Question[];
  }) {
    return (
      (this.incorrect.length > 0 || this.correct.length > 0) &&
      this.correct.length <
        getQuizModuleSlides({
          quizModuleId: this.quizModuleId,
          age: settings.age,
          quizModules,
          questions,
        }).length
    );
  }

  hasCorrectlyAnswered(questionId: QuestionId) {
    return this.correct.includes(questionId);
  }

  addCorrectOrIncorrect(correctOrIncorrect: CorrectOrIncorrect, questionId: QuestionId) {
    this.correct = this.correct.filter((qId) => qId !== questionId);
    this.incorrect = this.incorrect.filter((qId) => qId !== questionId);
    this[correctOrIncorrect].push(questionId);
  }
}

export const getSerializedQuizModuleData = () => {
  return tryToParseStorageKeyValue("quizModules3") as Array<SerializedQuizModuleData>;
};

export const getStoredQuizModuleData = ({
  quizModule,
  quizModules,
}: {
  quizModule: QuizModuleId;
  quizModules: Record<string, QuizModule>;
}): QuizModuleData => {
  const serialisedQuizModuleData = getSerializedQuizModuleData();
  if (serialisedQuizModuleData === null)
    return new QuizModuleData({
      quizModules,
      moduleData: new SerializedQuizModuleData(quizModule),
    });

  for (let i = 0; i < serialisedQuizModuleData.length; i++) {
    const storedQuiz: QuizModuleData = new QuizModuleData({
      quizModules,
      moduleData: serialisedQuizModuleData[i],
    });

    if (storedQuiz.getId() === quizModule) {
      return storedQuiz;
    }
  }
  return new QuizModuleData({
    quizModules,
    moduleData: new SerializedQuizModuleData(quizModule),
  });
};

export const getStoredQuizModuleDatas = ({
  quizModules,
}: {
  quizModules: Record<string, QuizModule>;
}): Array<QuizModuleData> => {
  const serialisedQuizModuleData = getSerializedQuizModuleData();
  if (serialisedQuizModuleData === null) {
    return [];
  }

  const quizModuleData = serialisedQuizModuleData.map(
    // TODO COMPLETE: Add quiz modules
    (data) => new QuizModuleData({ moduleData: data, quizModules })
  );
  return quizModuleData;
};
